import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  Tooltip,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Colors } from "../../Styles/Colors";
import {
  CommonEditButton,
  CommonGridToolBarWithFilterText,
  DeletePopup,
} from "../../Components/Common Components/CommonComponents";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";

export default function SpecimenMaster() {
  const [specimen_master_data, setSpecimenMasterData] = useState([]);
  const [specimen_type, setSpecimenType] = useState("");
  const [specimen_code, setSpecimenCode] = useState("");
  const [specimen_prefix, setSpecimenPrefix] = useState("");
  const [is_active, setIsActive] = useState(true);
  const [edit_id, setEditId] = useState(null);
  const [is_loader, setLoader] = useState(false);
  const [selected_ids, setSelectedIds] = useState([]);
  const [is_inactive, setIsInactive] = useState(false);
  const [checkbox_list, setCheckboxList] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState("");
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState("");

  const successMessage = (message) => {
    setSuccessMsg(true);
    setSuccessMsgText(message);
  };

  const errorMessage = (message) => {
    setIsErrorMsg(true);
    setIsErrorMsgText(message);
  };

  const msgClose = () => {
    setIsErrorMsg(false);
    setIsErrorMsgText("");
    setSuccessMsg(false);
    setSuccessMsgText("");
  };

  const getTherapyMasterList = () => {
    try {
      setLoader(true);
      RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_SPECIMEN)
        .then((response) => {
          if (response?.data?.status === "success") {
            const data = response.data.data || [];
            setSpecimenMasterData(data.map((el, index) => ({ ...el, sno: index + 1 })));
          }
          setLoader(false);
        })
        .catch((e) => {
          setLoader(false);
          errorMessage(e?.response?.data?.message || e?.message);
        });
    } catch (e) {
      setLoader(false);
      errorMessage(e.message);
    }
  };

  useEffect(() => {
    getTherapyMasterList();
  }, []);

  const textBox = (label, name, width, disable = false) => {
    const value = name === "specimen_code" ? specimen_code : name === "specimen_prefix" ? specimen_prefix : specimen_type;
    return (
      <Box>
        <TextField
          size="small"
          name={name}
          disabled={disable}
          inputProps={{ maxLength: name === "specimen_prefix" ? 7 : 200 }}
          value={value || ""}
          sx={{ width, marginRight: "0.3vw" }}
          label={label}
          onChange={(e) => {
            if (name === "specimen_prefix" && e.target.value) {
              setSpecimenPrefix(e.target.value?.toUpperCase());
            } else if (name === "specimen_code") {
              setSpecimenCode(e.target.value);
            } else {
              setSpecimenType(e.target.value);
            }
          }}
          variant="outlined"
        />
      </Box>
    );
  };

  const handleTableCheckbox = (newSelectionModel) => {
    const ids = newSelectionModel?.type === "include" && newSelectionModel?.ids != null
      ? Array.from(newSelectionModel.ids)
      : [];
    setCheckboxList(ids);
    setSelectedIds(ids);
  };

  const rowSelectionModel = useMemo(
    () =>
      Array.isArray(checkbox_list) && checkbox_list.length > 0
        ? { type: "include", ids: new Set(checkbox_list) }
        : { type: "include", ids: new Set() },
    [checkbox_list]
  );

  const grpInactive = () => setIsInactive(true);

  const gridToolBar = () => (
    <Box>
      <CommonGridToolBarWithFilterText
        fromStore
        isDelete={selected_ids?.length > 0}
        DeleteBtnText="Inactive"
        removeData={grpInactive}
      />
    </Box>
  );

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      flex: 0.1,
      renderCell: ({ row }) => <Box component="div">{row?.sno ?? "-"}</Box>,
    },
    {
      field: "specimen_code",
      headerName: "Specimen Type Code",
      flex: 0.2,
      renderCell: ({ row }) => <Box component="div">{row?.specimen_code ?? "-"}</Box>,
    },
    {
      field: "specimen_prefix",
      headerName: "Prefix",
      flex: 0.2,
      renderCell: ({ row }) => <Box component="div">{row?.specimen_prefix ?? "-"}</Box>,
    },
    {
      field: "specimen_type",
      headerName: "Specimen Type",
      flex: 0.5,
      renderCell: ({ row }) =>
        row.specimen_type ? (
          row.specimen_type?.length > 70 ? (
            <Tooltip placement="top" title={row.specimen_type}>
              <Box fontSize="0.9dvw">{row.specimen_type?.slice(0, 70) + "..."}</Box>
            </Tooltip>
          ) : (
            <Box fontSize="0.9dvw">{row.specimen_type}</Box>
          )
        ) : (
          "-"
        ),
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 0.2,
      renderCell: ({ row }) => <Box component="div">{row?.is_active ? "Active" : "InActive"}</Box>,
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.1,
      renderCell: ({ row }) => (
        <Stack direction="row" alignItems="center" spacing={4}>
          <Stack direction="row" justifyContent="space-evenly" alignItems="center" spacing={1}>
            <CommonEditButton
              size="1.5vw"
              disable={selected_ids?.length > 0}
              onClick={() => {
                setSpecimenCode(row.specimen_code || "");
                setSpecimenType(row.specimen_type || "");
                setSpecimenPrefix(row.specimen_prefix || "");
                setIsActive(row.is_active);
                setEditId(row.id);
              }}
            />
          </Stack>
        </Stack>
      ),
    },
  ];

  const clearFunction = () => {
    setSpecimenCode("");
    setSpecimenType("");
    setSpecimenPrefix("");
    setIsActive(true);
    setEditId(null);
    setLoader(false);
    setSelectedIds([]);
    setIsInactive(false);
    setCheckboxList([]);
  };

  const addTherapyDetails = () => {
    try {
      const data = {
        specimen_type,
        specimen_prefix,
        is_active,
      };
      if (edit_id) data.id = edit_id;
      RestAPIService.create(data, Serviceurls.HISTOPATHOLOGY_SPECIMEN)
        .then((response) => {
          if (response.data.status === "success") {
            successMessage(response.data.message);
            clearFunction();
            getTherapyMasterList();
          }
        })
        .catch((e) => {
          setLoader(false);
          errorMessage(e?.response?.data?.message || e?.message);
        });
    } catch (e) {
      errorMessage(e.message);
    }
  };

  const inactiveTherapyMaster = () => {
    const idsToInactive = Array.isArray(selected_ids) ? [...selected_ids] : [];
    inactiveClsPop(); // close popup immediately
    if (idsToInactive.length === 0) {
      errorMessage("No rows selected.");
      return;
    }
    try {
      RestAPIService.delete(
        `${Serviceurls.HISTOPATHOLOGY_SPECIMEN}?ids=${idsToInactive.join(",")}`,
        {}
      )
        .then((response) => {
          if (response.data.status === "success") {
            successMessage(response.data.message);
            getTherapyMasterList();
            clearFunction();
          }
        })
        .catch((e) => errorMessage(e?.response?.data?.message || e?.message));
    } catch (e) {
      errorMessage(e.message);
    }
  };

  const inactiveClsPop = () => {
    setIsInactive(false);
    setCheckboxList([]);
    setSelectedIds([]);
  };

  const rows = specimen_master_data;

  return (
    <Box component="div" sx={{ height: "79.5vh", width: "100%", backgroundColor: Colors.Background, padding: "0.5vw", boxSizing: "border-box" }}>
      <Box component="div" sx={{ height: "15.5vh", width: "100%", backgroundColor: "white", padding: "0.6vw", boxSizing: "border-box" }}>
        <Box component="div" sx={{ display: "flex", flexDirection: "row" }}>
          <Box component="div" sx={{ display: "flex", flexDirection: "row" }}>
            {textBox("Specimen Type Code", "specimen_code", "15vw", true)}
            {textBox("Prefix", "specimen_prefix", "15vw")}
            {textBox("Specimen Type *", "specimen_type", "30vw")}
          </Box>
        </Box>
        <Box sx={{ display: "flex", marginTop: "1vw", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={is_active}
                  onChange={(e) => setIsActive(e.target.checked)}
                  size="small"
                  disabled={!edit_id}
                />
              }
              label="Active"
            />
          </Box>
          <Box sx={{ gap: "1vw" }}>
            <Button size="small" variant="outlined" sx={{ textTransform: "capitalize", width: "7vw" }} onClick={clearFunction}>
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{ textTransform: "capitalize", width: "7vw", marginLeft: "1vw" }}
              disabled={!specimen_type?.trim() || !specimen_prefix?.trim()}
              onClick={addTherapyDetails}
            >
              {edit_id ? "Update" : "Add"}
            </Button>
          </Box>
        </Box>
      </Box>
      <Box component="div" sx={{ height: "61vh", width: "100%", marginTop: "0.5vw", backgroundColor: "white", boxSizing: "border-box" }}>
        <Box sx={{ height: "61vh", marginTop: "0.5vw", width: "100%" }}>
          <DataGrid
            sx={{ width: "100%" }}
            checkboxSelection
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleTableCheckbox}
            isRowSelectable={(params) => params.row.is_active}
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id ?? row.sno}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            showToolbar
            localeText={{
              toolbarColumns: "",
              toolbarDensity: "",
              toolbarExport: "",
              toolbarFilters: "",
              toolbarExportPrint: "",
            }}
            hideFooter
            slots={{
              toolbar: gridToolBar,
              noRowsOverlay: () => (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                  No Records To Be Shown
                </Box>
              ),
            }}
            pageSizeOptions={[10, 20, 30]}
            headerHeight={40}
            rowHeight={40}
            density='standard'
            disableRowSelectionOnClick
            loading={is_loader}
          />
        </Box>
      </Box>
      {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      {is_inactive && (
        <DeletePopup
          DeletTitle="Are you sure, Do you want to InActive this specimen ?"
          deleteAlertPopupClose={inactiveClsPop}
          removeData={inactiveTherapyMaster}
          ButtonText="Inactive"
        />
      )}
    </Box>
  );
}
